import type { EmissionReportData, EmissionReportLogEntry } from '../../types/entities';

/**
 * Reine Zaehlung ueber `resultData` (Plan 35-11, Sketch 020). Kein React-, Router- oder
 * Mantine-Bezug -- das Modul laeuft unter `environment: 'node'`.
 *
 * Die 22 Messwertzeilen einer Messung liegen unter `measurement.values` (ein Eintrag je
 * Portalfeld, das der Import tatsaechlich befuellen kann -- Be/Se/Te/Zn kommen dort NIE vor,
 * siehe `.claude/memory/emissionsbericht-import.md`). `valueBasis`/`oxygenReference` sind
 * EIGENE Attribute der Messung, nicht Teil von `values`, und zaehlen deshalb nie in `cellCount`
 * mit -- die Bezugsgroessen sind Formularfelder, aber keine Messwerte (Sketch 020,
 * Entscheidung 4: "aus der Statistik bleiben sie heraus").
 */

export interface PreviewStats {
  /**
   * Zahl der Messungen, die tatsaechlich uebernommen wuerden -- ausgeschlossene Messungen
   * (Punkt 4, WR-15/WR-12: `excluded === true`) zaehlen NICHT mit, obwohl sie weiterhin im Array
   * stehen. Der Knopf „N Messungen übernehmen" und die Fusszeile lesen ausschliesslich diesen Wert.
   */
  measurementCount: number;
  /** Zahl der Zellen -- NUR die Messwertzeilen, die beiden Bezugsgroessen zaehlen nicht mit. */
  cellCount: number;
  /** Zahl der Zellen mit einem Wert (`value !== null`), egal ob unter der Bestimmungsgrenze. */
  acceptedCount: number;
  /** Davon mit dem `<`-Kennzeichen (Teilmenge von `acceptedCount`, kein Sonderfall). */
  belowLimitCount: number;
  /** `cellCount - acceptedCount` -- Zellen ohne Wert. */
  emptyCount: number;
  /**
   * Protokolleintraege mit Schwere `unread` (D-04) -- die "Nicht gelesen"-Liste der Vorschau
   * (Sketch 020, Entscheidung 2). Kommt aus `resultData.log`, NICHT aus den Zellen: die
   * Meldung im Protokoll ist die Auskunft, die angezeigt wird. Die Zellenmarkierung
   * `empty === 'unread'` fliesst in keine Zaehlung mehr ein (Durchsprache 14.09.2026, Punkt 9)
   * -- den aktuellen Stand einer Zelle zeigt die Matrix selbst (Tooltip `cellTitle`).
   */
  unreadEntries: EmissionReportLogEntry[];
}

/** Alle Zaehlungen auf null/leer -- fuer `data === null` und einen frisch abgelegten Eintrag. */
function emptyStats(): PreviewStats {
  return {
    measurementCount: 0,
    cellCount: 0,
    acceptedCount: 0,
    belowLimitCount: 0,
    emptyCount: 0,
    unreadEntries: [],
  };
}

export function computePreviewStats(data: EmissionReportData | null): PreviewStats {
  if (data === null) {
    return emptyStats();
  }

  let cellCount = 0;
  let acceptedCount = 0;
  let belowLimitCount = 0;

  // Punkt 4: ausgeschlossene Messungen fliessen weder in die Messungszahl noch in eine der
  // Zellzaehlungen ein -- sie bleiben nur im Dokument stehen, um widerrufbar zu sein.
  const includedMeasurements = data.measurements.filter((measurement) => measurement.excluded !== true);

  for (const measurement of includedMeasurements) {
    for (const cell of Object.values(measurement.values)) {
      cellCount += 1;
      if (cell.value !== null) {
        acceptedCount += 1;
        if (cell.belowLimit) {
          belowLimitCount += 1;
        }
      }
    }
  }

  return {
    measurementCount: includedMeasurements.length,
    cellCount,
    acceptedCount,
    belowLimitCount,
    emptyCount: cellCount - acceptedCount,
    unreadEntries: data.log.filter((entry) => entry.severity === 'unread'),
  };
}
